import axios, { type CancelTokenSource } from 'axios';
import { useEffect, useRef } from 'react';
import { useQuery } from 'react-query';
import { ApiClientError } from 'api/client';
import { SuggestionApi } from 'api/suggestions';
import { queryKey } from 'constants/queryKey';
import useDebounce from 'utils/hooks/useDebounce';
import {
  TEXT_EDITOR_SUGGESTIONS_DEBOUNCE_DELAY,
  TEXT_EDITOR_SUGGESTIONS_QUERY_START_LENGTH,
} from 'app/modules/textEditor/constants/textEditorFeature';
import type {
  MentionItem,
  MentionPartialReason,
  MentionSearchEnvelope,
  MentionSearchFailure,
} from 'api/mentions/types';

export type MentionSuggestionsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ok'; items: MentionItem[] }
  | {
      status: 'empty';
      query: string;
      notice: string | null;
      partialReason: MentionPartialReason | null;
    }
  | { status: 'partial'; items: MentionItem[]; notice: string }
  | { status: 'failed'; failure: MentionSearchFailure }
  | { status: 'transportError' };

interface Params {
  pageId: number;
  pageType: number;
  query: string;
}

export function useMentionSuggestions({
  pageId,
  pageType,
  query,
}: Params): MentionSuggestionsState {
  const debouncedSearch = useDebounce(
    query.replaceAll('_', ' '),
    TEXT_EDITOR_SUGGESTIONS_DEBOUNCE_DELAY,
  );
  const enabled =
    debouncedSearch.length >= TEXT_EDITOR_SUGGESTIONS_QUERY_START_LENGTH;
  const source = useRef<CancelTokenSource>();

  useEffect(() => () => source.current?.cancel(), []);

  const { data, error, isLoading } = useQuery<MentionSearchEnvelope>(
    queryKey.textEditorSuggestions([debouncedSearch, pageType, pageId]),
    () => {
      source.current?.cancel();
      source.current = axios.CancelToken.source();

      return SuggestionApi.mentions({
        query: debouncedSearch,
        pageId,
        cancelToken: source.current.token,
      });
    },
    { enabled },
  );

  if (!enabled) {
    return { status: 'idle' };
  }

  if (error) {
    const cancelled =
      error instanceof ApiClientError
        ? axios.isCancel(error.axiosError)
        : axios.isCancel(error);

    return cancelled ? { status: 'loading' } : { status: 'transportError' };
  }

  if (isLoading || !data) {
    return { status: 'loading' };
  }

  if (data.failure) {
    return { status: 'failed', failure: data.failure };
  }

  if (data.items.length === 0) {
    // The notice travels with an EMPTY result too, and dropping it here was
    // the whole defect: composing as a LinkedIn personal profile, the user
    // types the name of the person they want to tag, which matches no
    // company, so this is the only branch they ever reach -- and the only
    // place the server's explanation could have reached them.
    return {
      status: 'empty',
      query: debouncedSearch,
      notice: data.notice,
      partialReason: data.partialReason,
    };
  }

  return data.notice
    ? { status: 'partial', items: data.items, notice: data.notice }
    : { status: 'ok', items: data.items };
}
